import { useState } from "react";
import { Button } from "@/components/ui/PageHeader";
import { assist, type AssistanceFeature, type AssistanceIntent, type AssistanceResponse } from "@/api/composer";
import { KeptWorkPanel } from "./KeptWorkPanel";
import { AssistanceComparisonPanel } from "./AssistanceComparisonPanel";
import { AssistanceGuidancePanel } from "./AssistanceGuidancePanel";
import { ContributionReviewPanel } from "./ContributionReviewPanel";

/** This panel tests the policy contract, not the proposed consumer layout. */
export function AssistancePreview({ text }: { text: string }) {
  const [receipt, setReceipt] = useState<{ text: string; data: AssistanceResponse } | null>(null);
  const result = receipt?.text === text ? receipt.data : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState("");
  const [features, setFeatures] = useState<AssistanceFeature[]>([]);

  async function preview(intentChoice?: AssistanceIntent) {
    setBusy(true);
    setError(null);
    setReceipt(null);
    try {
      const data = (await assist(text, intentChoice, query.trim() ? {
        query: query.trim(), features: features.length ? features : undefined,
        city: city.trim() || undefined, state: region.trim() || undefined,
        year: year ? Number(year) : undefined,
      } : undefined)).data;
      setReceipt({ text, data });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Assistance preview failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-4 rounded-2xl border border-gray-200 bg-white p-5" aria-label="Private assistance preview">
      <h2 className="font-display text-lg font-semibold text-gray-900">Private assistance</h2>
      <p className="mt-1 text-sm text-gray-600">
        Tests intent, destination and permission-checked sources. Assistance does not save or publish; Keep below is an explicit action.
        The Classify and Submit controls are a separate flow.
      </p>
      <fieldset className="mt-3 space-y-2 text-sm">
        <legend className="font-semibold">Optional source lookup</legend>
        <label className="block">Search phrase (required to use these filters)
          <input className="mt-1 block w-full rounded border p-2" maxLength={300}
            value={query} onChange={event => setQuery(event.target.value)} />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label>City<input className="block w-full rounded border p-2" value={city}
            maxLength={100} onChange={event => setCity(event.target.value)} /></label>
          <label>State<input className="block w-full rounded border p-2" value={region}
            maxLength={100} onChange={event => setRegion(event.target.value)} /></label>
          <label>Calendar year<input className="block w-full rounded border p-2" value={year}
            type="number" min={2024} max={2030} onChange={event => setYear(event.target.value)} /></label>
        </div>
        <p>Choose up to three features, or use the classified destination.</p>
        <div className="flex flex-wrap gap-3">
          {(["curious", "awaaz", "providers", "events", "utsav", "institutions", "dharohar", "yaadein", "people"] as const).map(feature => (
            <label key={feature}><input type="checkbox" checked={features.includes(feature)}
              disabled={!features.includes(feature) && features.length >= 3}
              onChange={event => setFeatures(current => event.target.checked ? [...current, feature]
                : current.filter(value => value !== feature))} /> {feature}</label>
          ))}
        </div>
      </fieldset>
      <div className="mt-3">
        <Button variant="secondary" size="sm" disabled={busy || text.trim().length < 3 || text.trim().length > 10000}
          isLoading={busy} onClick={() => preview()}>Preview assistance</Button>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      {result && (
        <div className="mt-4 space-y-3 text-sm" aria-live="polite">
          <p>{result.message}</p>
          <AssistanceGuidancePanel guidance={result.guidance} disabled={busy} onChoose={search => {
            setQuery(search.query); setFeatures(search.features); setCity(search.city ?? ""); setRegion(search.state ?? "");
            setYear("");
          }} />
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt>Response</dt><dd>{result.responseType}</dd>
            <dt>Member intent</dt><dd>{result.memberIntent ?? "Unresolved"}</dd>
            <dt>Destination action</dt><dd>{result.classification.action}</dd>
            <dt>Model</dt><dd>{result.classification.modelVersion}</dd>
            <dt>Fallback</dt><dd>{result.classification.fallbackReason ?? "None"}</dd>
            <dt>Handoff</dt><dd>{result.handoff ? `${result.handoff.feature} / ${result.handoff.entry}` : "None"}</dd>
            <dt>Review required</dt><dd>{result.handoff?.requiresReview ? "Yes, in the destination feature" : "No contribution prepared"}</dd>
            <dt>Retrieval</dt><dd>{result.capabilities.retrieval}</dd>
            <dt>Keep</dt><dd>{result.capabilities.keptWork ? "Available" : "Not implemented"}</dd>
          </dl>
          {result.retrieval.lanes.map(lane => <p key={lane.feature}>{lane.feature}: {lane.status} ({lane.count})</p>)}
          {result.retrieval.sources.map(source => (
            <article key={`${source.reference.type}:${source.reference.id}`} className="rounded-lg border p-3">
              <h3 className="font-semibold">{source.title}</h3>
              {source.excerpt && <p>{source.excerpt}</p>}
              <p className="mt-1 break-all text-xs">{source.destinationPath}</p>
              <p className="text-xs">{source.reference.type} · {source.reference.id}</p>
              {source.parentReference && <p className="text-xs">Within {source.parentReference.type} · {source.parentReference.id}</p>}
              <p className="text-xs">Access checked: {source.retrievedAt}</p>
            </article>
          ))}
          {result.retrieval.notes.map(note => <p key={note} className="text-xs text-gray-600">{note}</p>)}
          {result.responseType === "feature_handoff" && (result.memberIntent === "find" || result.memberIntent === "plan")
            && result.retrieval.sources.length > 0 && <AssistanceComparisonPanel text={text} intent={result.memberIntent}
              sources={result.retrieval.sources} disabled={busy} />}
          {result.choices.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {result.choices.map(choice => (
                <Button key={choice.intent} size="sm" variant="secondary" disabled={busy}
                  onClick={() => preview(choice.intent)}>{choice.label}</Button>
              ))}
            </div>
          )}
          {result.handoff?.entry === "compose" && (["awaaz", "curious", "events", "yaadein"] as const)
            .filter(feature => feature === result.handoff?.feature)
            .map(feature => <ContributionReviewPanel key={feature} text={result.handoff!.requestText} feature={feature} />)}
          {result.resources.map(resource => <p key={resource.name}>{resource.name}: {resource.contact}</p>)}
          <p className="text-xs text-gray-500">This preview does not open a feature or execute its action.</p>
        </div>
      )}
      <KeptWorkPanel
        initial={{ title: text.slice(0, 160) || "My next step", originalRequest: text,
          memberIntent: result?.memberIntent === "plan" ? "plan" : result?.memberIntent === "contribute" ? "contribute" : "find",
          feature: result?.handoff?.feature ?? null, constraints: [],
          sourceReferences: result?.retrieval.sources.map(source => source.reference) ?? [], draft: null, unresolvedQuestions: [] }} />
    </section>
  );
}
